import { ICharacterDto, ICharacterProgressionDto } from '../../../shared/interfaces';

type UnknownRecord = Record<string, unknown>;

export class CharacterContractError extends Error {
    constructor(message: string, public readonly payload?: unknown) {
        super(message);
        this.name = 'CharacterContractError';
    }
}

const first = (record: UnknownRecord | null | undefined, ...keys: string[]) => {
    for (const key of keys) {
        const value = record?.[key];
        if (value !== undefined && value !== null) return value;
    }
    return undefined;
};

const numberOr = (value: unknown, fallback: number) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const textOr = (value: unknown, fallback = '') => {
    const text = String(value ?? '').trim();
    return text || fallback;
};

const mapProgression = (raw: UnknownRecord | null | undefined, fallbackLevel: number): ICharacterProgressionDto | undefined => {
    if (!raw) return undefined;
    return {
        globalLevel: numberOr(first(raw, 'GlobalLevel', 'globalLevel'), fallbackLevel),
        mmorpgLevel: numberOr(first(raw, 'MMORPGLevel', 'mmorpgLevel'), 1),
        mmorpgRankName: textOr(first(raw, 'MMORPGRankName', 'mmorpgRankName')),
        arLevel: numberOr(first(raw, 'ARLevel', 'arLevel'), 1),
        arRankName: textOr(first(raw, 'ARRankName', 'arRankName')),
        trainingLevel: numberOr(first(raw, 'TrainingLevel', 'trainingLevel'), 1),
        trainingRankName: textOr(first(raw, 'TrainingRankName', 'trainingRankName')),
        cognitiveLevel: numberOr(first(raw, 'CognitiveLevel', 'cognitiveLevel'), 1),
        cognitiveRankName: textOr(first(raw, 'CognitiveRankName', 'cognitiveRankName')),
        vrLevel: numberOr(first(raw, 'VRLevel', 'vrLevel'), 1),
        vrRankName: textOr(first(raw, 'VRRankName', 'vrRankName')),
        wearLevel: numberOr(first(raw, 'WearLevel', 'wearLevel'), 1),
        wearRank: numberOr(first(raw, 'WearRank', 'wearRank'), 1),
        wearXp: numberOr(first(raw, 'WearXP', 'wearXp'), 0),
        wearRankName: textOr(first(raw, 'WearRankName', 'wearRankName')),
        spiroLevel: numberOr(first(raw, 'SpiroLevel', 'spiroLevel'), 1),
        spiroRankName: textOr(first(raw, 'SpiroRankName', 'spiroRankName')),
        fineMotorLevel: numberOr(first(raw, 'FineMotorLevel', 'fineMotorLevel'), 1),
        fineMotorRank: numberOr(first(raw, 'FineMotorRank', 'fineMotorRank'), 1),
        fineMotorStage: numberOr(first(raw, 'FineMotorStage', 'fineMotorStage'), 1),
        fineMotorXp: numberOr(first(raw, 'FineMotorXP', 'fineMotorXp'), 0),
        fineMotorRankName: textOr(first(raw, 'FineMotorRankName', 'fineMotorRankName')),
    };
};

export const mapCharacterDto = (raw: unknown): ICharacterDto => {
    if (!raw || typeof raw !== 'object') {
        throw new CharacterContractError('Character response item must be an object.', raw);
    }
    const source = raw as UnknownRecord;
    const id = numberOr(first(source, 'ID', 'id'), Number.NaN);
    if (!Number.isFinite(id) || id <= 0) {
        throw new CharacterContractError('Character response does not contain a valid ID.', raw);
    }

    const level = numberOr(first(source, 'Level', 'level'), 1);
    const progressionRaw = first(source, 'Progression', 'progression') as UnknownRecord | undefined;
    const image = textOr(first(source, 'Image', 'image'));
    const model3dUrl = textOr(first(
        source,
        'Model3DURL', 'model3dUrl', 'Model3dURL', 'model3DUrl',
        'ModelURL', 'modelUrl', 'GLBURL', 'glbUrl', 'AvatarModelURL', 'avatarModelUrl',
    ));
    const csvFilesRaw = first(source, 'CSVFiles', 'csvFiles');

    return {
        id,
        userId: textOr(first(source, 'UserID', 'userId')),
        name: textOr(first(source, 'Name', 'name'), 'Unknown'),
        level,
        race: textOr(first(source, 'Race', 'race'), 'Unknown'),
        charClass: textOr(first(source, 'CharClass', 'charClass'), 'Unknown'),
        gender: textOr(first(source, 'Gender', 'gender'), 'Unknown'),
        guild: textOr(first(source, 'Guild', 'guild')),
        portrait: textOr(first(source, 'Portrait', 'portrait'), image),
        image,
        model3dUrl: model3dUrl || undefined,
        updatedAt: textOr(first(source, 'UpdatedAt', 'updatedAt')),
        csvFiles: Array.isArray(csvFilesRaw)
            ? csvFilesRaw as ICharacterDto['csvFiles']
            : [],
        progression: mapProgression(progressionRaw, level),
    };
};

export const mapCharacterList = (raw: unknown): ICharacterDto[] => {
    if (!Array.isArray(raw)) {
        throw new CharacterContractError('Characters response must be an array.', raw);
    }
    return raw.map(mapCharacterDto);
};
